import { LoginRequest, AuthResponse, User, CreateUserRequest, UpdateUserRequest, ChangePasswordRequest } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: User | null = null;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
        this.clearStorage();
      }
    }
  }

  private saveToStorage(authResponse: AuthResponse) {
    this.accessToken = authResponse.accessToken;
    this.refreshToken = authResponse.refreshToken;
    this.user = authResponse.user;

    localStorage.setItem('accessToken', authResponse.accessToken);
    localStorage.setItem('refreshToken', authResponse.refreshToken);
    localStorage.setItem('user', JSON.stringify(authResponse.user));
  }

  private clearStorage() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const authResponse: AuthResponse = await response.json();
    this.saveToStorage(authResponse);
    return authResponse;
  }

  async refreshTokens(): Promise<AuthResponse | null> {
    if (!this.refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) {
        this.clearStorage();
        return null;
      }

      const authResponse: AuthResponse = await response.json();
      this.saveToStorage(authResponse);
      return authResponse;
    } catch (error) {
      this.clearStorage();
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      this.clearStorage();
    }
  }

  async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as any)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // If we get a 401 or 403, try to refresh the token
    if ((response.status === 401 || response.status === 403) && this.refreshToken) {
      const refreshResult = await this.refreshTokens();
      if (refreshResult) {
        (headers as any)['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(url, {
          ...options,
          headers,
        });
      }
    }

    return response;
  }

  // User management methods (admin only)
  async getAllUsers(): Promise<User[]> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users`);

    if (!response.ok) {
      throw new Error('Failed to load users');
    }

    return response.json();
  }

  async getUserById(id: number): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users/${id}`);

    if (!response.ok) {
      throw new Error('User not found');
    }

    return response.json();
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create user');
    }

    return response.json();
  }

  async updateUser(id: number, userData: UpdateUserRequest): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user');
    }

    return response.json();
  }

  async deleteUser(id: number): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user');
    }
  }

  /**
   * Change password for the currently authenticated user.
   * Uses the /auth/change-password endpoint (no admin role required).
   */
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }
  }

  /**
   * Admin: Change password for any user by ID.
   * Uses the /users/{id}/change-password endpoint (admin role required).
   */
  async changeUserPassword(userId: number, passwordData: ChangePasswordRequest): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users/${userId}/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }
  }

  // Getters
  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  getCurrentUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.accessToken !== null && this.user !== null;
  }

  isAdmin(): boolean {
    return this.user?.role === 'ADMIN';
  }

  hasRole(role: string): boolean {
    return this.user?.role === role;
  }
}

export const authService = new AuthService();
export default authService;
